import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { adminDataApi, type BenefitApplicationDto, type CaseRecordDto } from "../../services/admin-data-api";

const statusColor: Record<string, "default" | "success" | "warning" | "error"> = {
  PENDING: "warning",
  APPROVED: "success",
  REJECTED: "error"
};

const formatDateTime = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));

export const ApplicationsPage = () => {
  const [applications, setApplications] = useState<BenefitApplicationDto[]>([]);
  const [cases, setCases] = useState<CaseRecordDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<BenefitApplicationDto | null>(null);
  const [form, setForm] = useState({
    applicantName: "",
    applicantEmail: "",
    program: "",
    caseId: ""
  });
  const [isSaving, setIsSaving] = useState(false);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [applicationsData, casesData] = await Promise.all([
        adminDataApi.getApplications(),
        adminDataApi.getCases()
      ]);
      setApplications(applicationsData);
      setCases(casesData.cases); // Extract cases array from response
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData().catch(() => {
      setError("Unable to load data");
      setIsLoading(false);
    });
  }, []);

  const handleOpenCreate = () => {
    setSelectedApplication(null);
    setForm({
      applicantName: "",
      applicantEmail: "",
      program: "",
      caseId: ""
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (application: BenefitApplicationDto) => {
    setSelectedApplication(application);
    setForm({
      applicantName: application.applicantName,
      applicantEmail: application.applicantEmail,
      program: application.program,
      caseId: application.case?.id ?? ""
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedApplication(null);
    setForm({
      applicantName: "",
      applicantEmail: "",
      program: "",
      caseId: ""
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      if (selectedApplication) {
        await adminDataApi.updateApplication(selectedApplication.id, {
          applicantName: form.applicantName,
          applicantEmail: form.applicantEmail,
          program: form.program,
          caseId: form.caseId || null
        });
        setSuccessMessage("Application updated successfully");
      } else {
        await adminDataApi.createApplication({
          applicantName: form.applicantName,
          applicantEmail: form.applicantEmail,
          program: form.program,
          caseId: form.caseId || undefined
        });
        setSuccessMessage("Application created successfully");
      }
      await loadData();
      handleCloseDialog();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save application");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateStatus = async (id: string, status: string) => {
    setError(null);
    setSuccessMessage(null);
    try {
      await adminDataApi.updateApplication(id, { status });
      setSuccessMessage(`Application ${status.toLowerCase()} successfully`);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to update application status");
    }
  };

  return (
    <>
      <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Grid item>
          <Typography variant="h4" component="h1">
            Benefit Applications
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Review recently submitted applications and their current status.
          </Typography>
        </Grid>
        <Grid item>
          <Button variant="contained" onClick={handleOpenCreate}>
            New Application
          </Button>
        </Grid>
      </Grid>

      {successMessage ? (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {isLoading ? <CircularProgress sx={{ display: "block", my: 4 }} /> : null}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Applicant</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Program</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Linked Case</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {applications.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>{item.applicantName}</TableCell>
                <TableCell>{item.applicantEmail}</TableCell>
                <TableCell>{item.program}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={statusColor[item.status] ?? "default"}
                    label={item.status}
                  />
                </TableCell>
                <TableCell>{item.case?.title ?? "Not linked"}</TableCell>
                <TableCell>{formatDateTime(item.submittedAt)}</TableCell>
                <TableCell align="right">
                  <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEdit(item)}
                      title="Edit application"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {item.status === "PENDING" ? (
                      <>
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleUpdateStatus(item.id, "APPROVED")}
                          title="Approve"
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleUpdateStatus(item.id, "REJECTED")}
                          title="Reject"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : null}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && applications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No applications found. Use "New Application" to get started.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedApplication ? "Edit Application" : "Create Application"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                label="Applicant Name"
                value={form.applicantName}
                onChange={(e) => setForm((prev) => ({ ...prev, applicantName: e.target.value }))}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Applicant Email"
                type="email"
                value={form.applicantEmail}
                onChange={(e) => setForm((prev) => ({ ...prev, applicantEmail: e.target.value }))}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Program"
                value={form.program}
                onChange={(e) => setForm((prev) => ({ ...prev, program: e.target.value }))}
                required
                fullWidth
                placeholder="e.g. SEED, BRISP"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                label="Linked Case (Optional)"
                value={form.caseId}
                onChange={(e) => setForm((prev) => ({ ...prev, caseId: e.target.value }))}
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {cases.map((caseItem) => (
                  <MenuItem key={caseItem.id} value={caseItem.id}>
                    {caseItem.title}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isSaving || !form.applicantName || !form.applicantEmail || !form.program}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
